/* eslint-disable @next/next/no-img-element */
import { useEffect, useState } from 'react'
import { socialLinks } from 'pages/index'
import { classNames } from 'helpers/utilities'
import type { CountdownTimer } from 'hooks/useCountdown'

const DateTimeDisplay = ({ value, dim = false }: { value: number; dim?: boolean }) => {
  return (
    <div className="flex flex-col items-center">
      <p
        className={classNames(
          dim ? 'text-gray-600' : 'text-gray-50',
          'font-mono font-bold text-3xl'
        )}
      >
        {value.toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        })}
      </p>
    </div>
  )
}

const Countdown = ({ timer }: { timer: CountdownTimer }) => {
  const { days, hours, minutes, seconds } = timer

  return (
    <div className="p-6 flex items-center text-xl space-x-1 text-gray-300 font-mono">
      {days > 0 && (
        <>
          <DateTimeDisplay value={days} />
          <p>:</p>
        </>
      )}
      <DateTimeDisplay value={hours} dim={days + hours == 0} />
      <p>:</p>
      <DateTimeDisplay value={minutes} dim={days + hours + minutes == 0} />
      <p>:</p>
      <DateTimeDisplay value={seconds} />
    </div>
  )
}

type TimerProps = {
  countdown: number
  total: number
  timer: CountdownTimer
}

const CircleTimer = ({ countdown, total, timer }: TimerProps) => {
  const [percent, setPercent] = useState(0)

  const width = 320 // w-80, h-80
  const strokeWidth = 8
  const radius = width / 2 - strokeWidth * 2
  const xyPos = width / 2
  const circumference = radius * 2 * Math.PI
  const strokeDashoffset = circumference - (percent / 100) * circumference

  useEffect(() => {
    const pct = (countdown * 100) / total
    setPercent(pct)
  }, [countdown, total])

  return (
    <div className="inline-flex items-center justify-center overflow-hidden rounded-full bg-gray-800/30">
      <svg className="w-80 h-80 -rotate-90">
        <circle
          className="text-gray-800"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={xyPos}
          cy={xyPos}
        />
        <circle
          className="text-brand-500 animate-pulse"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={xyPos}
          cy={xyPos}
        />
      </svg>
      <span className="absolute">
        <div className="flex flex-col items-center justify-center">
          <a
            className="opacity-90 hover:opacity-100"
            href="https://nf.domains"
            target="_blank"
            rel="noreferrer"
          >
            <img src="/img/logo-nfd-white.svg" alt="NFD Logo" className="w-24" />
          </a>
          <span className="mt-2 text-brand-500 font-medium text-sm md:text-xs">Mainnet Launch</span>
          <Countdown timer={timer} />
          <img
            src="/img/logo-algorand-full-white.svg"
            alt="Algorand Logo"
            className="w-20 mt-5 mb-4 opacity-20"
          />
        </div>
      </span>
    </div>
  )
}

export default function LaunchTimer({ timer, countdown, total }: TimerProps) {
  return (
    <div className="h-screen bg-gray-900 flex flex-col justify-center">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="md:scale-125 xl:scale-150">
          <CircleTimer countdown={countdown} total={total} timer={timer} />
          <div className="mt-6 md:mt-4 flex items-center justify-center divide-x divide-gray-700 px-3">
            <p className="text-center text-sm md:text-xs xl:text-[10px] px-6 md:px-4">
              <a
                href="https://nf.domains/"
                className="text-gray-500 font-medium hover:text-gray-300"
                target="_blank"
                rel="noreferrer"
              >
                nf.domains
              </a>
            </p>
            <div className="flex justify-center space-x-5 md:order-2 md:space-x-3 px-6 md:px-4">
              {socialLinks.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-500 hover:text-gray-300"
                  target="_blank"
                  rel="noreferrer"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-6 w-6 md:h-5 md:w-5 xl:h-4 xl:w-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
